import React, { ReactElement } from "react";
import {
    Integration, IntegrationActionType, useGetAllIntegrationsQuery,
} from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

type PropClearable<TIsClearable, T> = TIsClearable extends true ? T | undefined : T;

export interface IntegrationsPickerProps<TIsMulti = false, TIsClearable = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    actions?: IntegrationActionType[];
    value: (TIsMulti extends true ? string[] : string) | undefined;
    isClearable?: boolean;
    onSelected: (sourceId: PropClearable<TIsClearable, (TIsMulti extends true ? string[] : string)>) => void;
}

const Integrations = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, actions, isClearable,
}: IntegrationsPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetAllIntegrationsQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<Integration>[] : Option<Integration>;
    type ValueType = TIsMulti extends true ? Option<Integration>[] : Option<Integration>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<Integration>)?.value) as Parameters<typeof onSelected>[0];
        onSelected(values);
    };

    let integrations = data?.integrations;

    if (integrations && actions) {
        const actionSet = new Set(actions);
        integrations = integrations.filter(int => int.actions?.some(action => actionSet.has(action)));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = integrations?.map(integration => ({
        label: integration.label as string,
        value: integration.id.toString() as string,
        data: integration,
    })) ?? [];

    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={values ?? []}
            value={selected as ValueType}
            isClearable={isClearable}
        />
    );
});

Integrations.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    actions: undefined,
    isClearable: undefined,
};

export default Integrations as <
    TMulti extends boolean = false,
> (
    props: IntegrationsPickerProps<TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof Integrations>;
